/**
 * O SOAN CUA HOP CHU VA CUA GHI CHU DINH - GIO CHI CON O GO.
 *
 * Mot ghi chu dinh la mot doan van ban dat tren trang; mot hop chu tu do cung
 * vay, chi khac hinh thuc thu ghi ra. Doan van ban do phai nhap o dau do, va cho
 * do khong the la window.prompt().
 *
 * =============================================================================
 *  KIEU CHU DA DON VE BANG THONG TIN, GIONG DUONG SUA CHU SAN CO
 * =============================================================================
 *  Truoc day o day con cong theo mot thanh kieu chu (phong, co chu, dam, nghieng,
 *  can le, mau chu). Nguoi dung bao bo tri lai giong duong sua chu: don het cac
 *  dieu khien do vao bang Thong tin o popup (ui/PropertiesPanel.tsx). O day chi
 *  con o go va nut Lưu / Hủy.
 *
 *  KIEU CHU do App giu (state `noteStyle`) va truyen xuong day CHI de ve o go cho
 *  dung co chu / mau / mat chu se ghi ra. Nut can le trong popup va phim tat
 *  Ctrl+Shift+L trong o go deu goi cung `onAlignChange` cua App. Mot lan ghi, mot
 *  bo gia tri: khong co duong nao de kieu chu va noi dung lech nhau.
 *
 * O day dat NGOAI .pageSurface, y het InlineTextEditor va vi cung mot ly do: be
 * mat trang cat bot phan tran (overflow: hidden), ma o soan cua mot ghi chu o
 * sat mep phai duoc phep tran ra ngoai.
 */
import { useState } from 'react';
import { TextEditorShell } from '../ui/TextEditorShell';
import { cssColor, type TextAlign } from '../ui/textEditCore';
import { getFontFamily, type FontFamilyId } from '../core/fonts/families';

/**
 * Kieu chu cua mot hop chu dang soan.
 *
 * Day la NOI DUNG se duoc ghi vao tep (mau chu, co chu, mat chu), khong phai
 * trang thai giao dien. App giu no (state noteStyle) va bang thuoc tinh o popup
 * sua no; o day chi doc de ve o go.
 */
export interface TextBoxDraftStyle {
  fontSizePt: number;
  align: TextAlign;
  bold: boolean;
  italic: boolean;
  /** Ma ho font (vd 'noto-sans'). Di vao doi tuong duoc tao, quyet dinh phong nhung. */
  fontFamily: FontFamilyId;
  textColorRgb: readonly [number, number, number];
}

export interface NotePopupProps {
  /** Vi tri neo, toa do CSS trong trang. */
  left: number;
  top: number;
  /** Noi dung dang co. Rong = doi tuong moi. */
  value: string;
  /** Tieu de o soan: "Ghi chú" hay "Hộp chữ". */
  title?: string;
  /** Kieu chu dang co - do App giu, CHI de ve o go va cho phim can le. */
  style: TextBoxDraftStyle;
  /** Phim tat can le (Ctrl+Shift+L/E/R) trong o go goi vao day. */
  onAlignChange: (align: TextAlign) => void;
  /** He so phong cua trang, de o go hien dung co chu se ghi ra. */
  zoom: number;
  onSave: (text: string) => void;
  onCancel: () => void;
}

export function NotePopup({
  left,
  top,
  value,
  title = 'Ghi chú',
  style,
  onAlignChange,
  zoom,
  onSave,
  onCancel,
}: NotePopupProps) {
  const [draft, setDraft] = useState(value);

  return (
    <TextEditorShell
      variant="object"
      left={left}
      top={top}
      title={title}
      draft={draft}
      onDraftChange={setDraft}
      placeholder={`Nhập nội dung ${title.toLowerCase()}…`}
      ariaLabel={`Nội dung ${title.toLowerCase()}`}
      // O go hien DUNG co chu, DUNG mau va DUNG mat chu se duoc ghi ra, nhan he
      // so phong cua trang.
      fontSizePx={style.fontSizePt * zoom}
      colorCss={cssColor(style.textColorRgb)}
      rows={3}
      bold={style.bold}
      italic={style.italic}
      fontFamilyCss={getFontFamily(style.fontFamily).cssStack}
      align={style.align}
      onAlignChange={onAlignChange}
      alignSupported
      canCommit
      commitLabel="Lưu"
      commitTitle="Lưu nội dung và kiểu chữ vào đối tượng này"
      onCommit={() => onSave(draft)}
      onCancel={onCancel}
      hint="Enter để lưu · Shift+Enter xuống dòng · Esc hủy"
    />
  );
}
